import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  InputLabel,
  FormControl,
  Paper,
  ImageList,
  ImageListItem,
  IconButton,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useAuth } from '../auth/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import config from '../config';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  useResizableColumns,
} from '../components/table/ResizableTable';
import ViewModeToggle from '../components/table/ViewModeToggle';
import DetailModalShell from '../components/detail/DetailModalShell';
import MockupsDataTabs from '../components/detail/MockupsDataTabs';
import { removeEntityById, upsertEntityById } from '../components/table/entityListPatch';

const debounceDelay = 300;
const PRODUCTS_TABLE_COLUMNS = [
  { key: 'select', label: '', minWidth: 56, maxWidth: 90, defaultWidth: 56, resizable: false },
  { key: 'mockup', label: 'Mockup', minWidth: 130, maxWidth: 260, defaultWidth: 140 },
  { key: 'image_filename', label: 'Image Filename', minWidth: 180, maxWidth: 420, defaultWidth: 240 },
  { key: 'product_type', label: 'Product Type', minWidth: 130, maxWidth: 280, defaultWidth: 160 },
  { key: 'variant_type', label: 'Variant Type', minWidth: 130, maxWidth: 280, defaultWidth: 160 },
  { key: 'product_title', label: 'Product Title', minWidth: 260, maxWidth: 700, defaultWidth: 360 },
  { key: 'status', label: 'Status', minWidth: 120, maxWidth: 240, defaultWidth: 140 },
];
const PRODUCT_PRICING_TABLE_COLUMNS = [
  { key: 'variant', label: 'Variant', minWidth: 220, maxWidth: 520, defaultWidth: 280 },
  { key: 'cost', label: 'Cost', minWidth: 120, maxWidth: 260, defaultWidth: 140 },
  { key: 'price', label: 'Price', minWidth: 180, maxWidth: 340, defaultWidth: 220 },
];

const getMockupSource = (image) => image?.source_url || image?.src || image?.SourceURL || '';
const getVariantLookupKey = (value) => String(value ?? '').trim();
const isProductPublished = (product) => {
  if (typeof product?.is_published === 'boolean') {
    return product.is_published;
  }
  return Boolean(product?.has_etsy_listing || product?.has_woocommerce_listing);
};
const getImageVariantIDs = (image) => {
  const raw =
    image?.variant_ids
    ?? image?.VariantIDs
    ?? image?.variantIds
    ?? image?.variant_ids_json
    ?? null;

  if (Array.isArray(raw)) {
    return raw
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) {
      return [];
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((value) => Number(value))
          .filter((value) => !Number.isNaN(value));
      }
    } catch {
      return trimmed
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => !Number.isNaN(value));
    }
  }

  return [];
};

const ProductsPage = () => {
  const { fetchWithAuth } = useAuth();
  const { latestJobUpdate, latestEntityUpdate } = useRealtime();
  const [products, setProducts] = React.useState([]);
  const [shops, setShops] = React.useState([]);
  const [loadingShops, setLoadingShops] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [productTypeFilter, setProductTypeFilter] = React.useState('all');
  const [variantTypeFilter, setVariantTypeFilter] = React.useState('all');
  const [productTypeOptions, setProductTypeOptions] = React.useState([]);
  const [variantTypeOptions, setVariantTypeOptions] = React.useState([]);
  const [syncing, setSyncing] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const selectedProductId = selectedProduct?.id || '';
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerLoading, setDrawerLoading] = React.useState(false);
  const [selectedShopId, setSelectedShopId] = React.useState('');
  const [actionMessage, setActionMessage] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const visibleProductIds = React.useMemo(
    () => products.map((product) => product.id).filter(Boolean),
    [products],
  );
  const visibleSelectedCount = React.useMemo(
    () => visibleProductIds.filter((id) => selectedIds.has(id)).length,
    [visibleProductIds, selectedIds],
  );
  const isAllSelected = visibleProductIds.length > 0 && visibleSelectedCount === visibleProductIds.length;
  const isPartiallySelected = visibleSelectedCount > 0 && visibleSelectedCount < visibleProductIds.length;
  const [sendModalOpen, setSendModalOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [generatingData, setGeneratingData] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [viewMode, setViewMode] = React.useState('tiles');
  const refreshTimerRef = React.useRef(null);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(PRODUCTS_TABLE_COLUMNS, {
    storageKey: 'table.printify-products',
  });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  const loadProducts = React.useCallback(async () => {
    if (!selectedShopId) {
      setProducts([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      if (productTypeFilter !== 'all') {
        params.set('product_type', productTypeFilter);
      }
      if (variantTypeFilter !== 'all') {
        params.set('variant_type', variantTypeFilter);
      }
      params.set('shop_id', selectedShopId);
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      const url = `${config.apiBaseUrl}/printify/products?${params.toString()}`;
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        throw new Error('Failed to load products');
      }
      const data = await response.json();
      setProducts(Array.isArray(data.items) ? data.items : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fetchWithAuth, selectedShopId, statusFilter, productTypeFilter, variantTypeFilter, rowsPerPage, page]);

  React.useEffect(() => {
    if (shops.length && !selectedShopId) {
      const preferred = shops.find((shop) => !shop.is_template) || shops[0];
      setSelectedShopId(preferred.id);
    }
  }, [shops, selectedShopId]);

  React.useEffect(() => {
    if (selectedShopId) {
      loadProducts();
    }
  }, [loadProducts, selectedShopId]);

  React.useEffect(() => {
    setPage(0);
  }, [selectedShopId, debouncedSearch, statusFilter, productTypeFilter, variantTypeFilter, rowsPerPage]);

  const loadShops = React.useCallback(async () => {
    setLoadingShops(true);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/shops`);
      if (!response.ok) {
        throw new Error('Failed to load shops');
      }
      const data = await response.json();
      setShops(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingShops(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadShops();
  }, [loadShops]);

  React.useEffect(() => {
    const loadTypeOptions = async () => {
      if (!selectedShopId) {
        setProductTypeOptions([]);
        setVariantTypeOptions([]);
        return;
      }
      try {
        const params = new URLSearchParams();
        params.set('shop_id', selectedShopId);
        const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/product-types?${params.toString()}`);
        if (!response.ok) {
          throw new Error('Failed to load product type options');
        }
        const payload = await response.json();
        setProductTypeOptions(Array.isArray(payload.product_types) ? payload.product_types : []);
        setVariantTypeOptions(Array.isArray(payload.variant_types) ? payload.variant_types : []);
      } catch (err) {
        console.error(err);
        setProductTypeOptions([]);
        setVariantTypeOptions([]);
      }
    };
    loadTypeOptions();
  }, [fetchWithAuth, selectedShopId]);

  React.useEffect(() => {
    if (!latestJobUpdate || latestJobUpdate.type !== 'job.updated') {
      return;
    }
    const job = latestJobUpdate.job;
    if (!job || Number(job.status) !== 2) {
      return;
    }
    const trackedJobs = new Set([
      'SyncPrintifyProducts',
      'SyncPrintifyAcrossShops',
      'DeletePrintifyProducts',
      'PublishPrintifyProduct',
      'SyncWooCommerceListings',
    ]);
    if (!trackedJobs.has(job.name)) {
      return;
    }
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      loadProducts();
    }, 500);
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
  }, [latestJobUpdate, loadProducts]);

  const productMatchesCurrentFilters = React.useCallback(
    (product) => {
      if (!product) {
        return false;
      }
      if (selectedShopId && product.shop?.id !== selectedShopId) {
        return false;
      }
      if (statusFilter === 'published' && !isProductPublished(product)) {
        return false;
      }
      if (statusFilter === 'unpublished' && isProductPublished(product)) {
        return false;
      }
      if (productTypeFilter !== 'all' && product.product_type !== productTypeFilter) {
        return false;
      }
      if (variantTypeFilter !== 'all' && product.variant_type !== variantTypeFilter) {
        return false;
      }
      if (debouncedSearch) {
        const searchValue = debouncedSearch.toLowerCase();
        const title = String(product.title || '').toLowerCase();
        if (!title.includes(searchValue)) {
          return false;
        }
      }
      return true;
    },
    [debouncedSearch, productTypeFilter, selectedShopId, statusFilter, variantTypeFilter],
  );

  React.useEffect(() => {
    if (!latestEntityUpdate) {
      return;
    }
    if (latestEntityUpdate.type !== 'entity.updated' && latestEntityUpdate.type !== 'entity.deleted') {
      return;
    }
    if (latestEntityUpdate.entity_type !== 'printify_product') {
      return;
    }
    const entityID = latestEntityUpdate.entity_id;
    if (!entityID) {
      return;
    }

    if (latestEntityUpdate.type === 'entity.deleted') {
      setProducts((prev) => removeEntityById(prev, entityID));
      setSelectedIds((prev) => {
        if (!prev.has(entityID)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(entityID);
        return next;
      });
      if (selectedProductId === entityID) {
        setDrawerOpen(false);
        setSelectedProduct(null);
      }
      return;
    }

    let cancelled = false;
    const refreshEntity = async () => {
      try {
        const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${entityID}?view=summary`);
        if (!response.ok) {
          if (response.status === 404) {
            setProducts((prev) => removeEntityById(prev, entityID));
          }
          return;
        }
        const payload = await response.json();
        const nextItem = payload?.item;
        if (!nextItem || cancelled) {
          return;
        }
        setProducts((prev) => {
          const exists = prev.some((item) => item.id === entityID);
          if (!exists) {
            return prev;
          }
          if (!productMatchesCurrentFilters(nextItem)) {
            return removeEntityById(prev, entityID);
          }
          return upsertEntityById(prev, nextItem);
        });

        if (drawerOpen && selectedProductId === entityID) {
          const detailResp = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${entityID}`);
          if (detailResp.ok) {
            const detailPayload = await detailResp.json();
            if (!cancelled && detailPayload?.item) {
              setSelectedProduct(detailPayload.item);
            }
          }
        }
      } catch (err) {
        console.warn('failed to refresh printify product entity', err);
      }
    };
    refreshEntity();
    return () => {
      cancelled = true;
    };
  }, [drawerOpen, fetchWithAuth, latestEntityUpdate, productMatchesCurrentFilters, selectedProductId]);

  const handleSync = React.useCallback(async () => {
    setSyncing(true);
    setError(null);
    setActionMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/sync`, {
        method: 'POST',
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Sync failed');
      }
      setActionMessage({ type: 'info', text: 'Printify sync queued. Progress is available in Team Jobs.' });
    } catch (err) {
      setError(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }, [fetchWithAuth]);

  const handleExport = React.useCallback(async () => {
    if (!selectedShopId) {
      setActionMessage({ type: 'error', text: 'Select a shop before exporting.' });
      return;
    }
    setExporting(true);
    setActionMessage(null);
    try {
      const url = `${config.apiBaseUrl}/printify/products/export?shop_id=${selectedShopId}`;
      const response = await fetchWithAuth(url, { timeoutMs: config.longRequestTimeoutMs });
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      link.download = match ? match[1] : 'printify-products.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setActionMessage({ type: 'success', text: 'Export completed.' });
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message || 'Export failed' });
    } finally {
      setExporting(false);
    }
  }, [fetchWithAuth, selectedShopId]);

  const handleImportClick = React.useCallback(() => {
    if (!fileInputRef.current) {
      return;
    }
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  }, []);

  const handleImportChange = React.useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      setImporting(true);
      setActionMessage(null);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/import`, {
          method: 'POST',
          body: formData,
          timeoutMs: config.longRequestTimeoutMs,
        });
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          throw new Error(body?.error || 'Import failed');
        }
        const payload = await response.json();
        const updatedCount = payload?.updated || 0;
        const errors = Array.isArray(payload?.errors) ? payload.errors : [];
        if (errors.length) {
          setActionMessage({
            type: 'warning',
            text: `Updated ${updatedCount} products. ${errors.length} rows had issues.`,
          });
        } else {
          setActionMessage({
            type: 'success',
            text: `Updated ${updatedCount} products successfully.`,
          });
        }
        await loadProducts();
      } catch (err) {
        setActionMessage({ type: 'error', text: err.message || 'Import failed' });
      } finally {
        setImporting(false);
      }
    },
    [fetchWithAuth, loadProducts],
  );

  const handleGenerateData = React.useCallback(async () => {
    if (selectedIds.size === 0) {
      setActionMessage({ type: 'error', text: 'Select at least one product.' });
      return;
    }
    setGeneratingData(true);
    setActionMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/generate-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: Array.from(selectedIds) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to trigger generation');
      }
      const queued = typeof payload.queued === 'number' ? payload.queued : selectedIds.size;
      setActionMessage({
        type: 'info',
        text: `Queued ${queued} product${queued === 1 ? '' : 's'} for AI metadata. Progress is available in Team Jobs.`,
      });
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message || 'Failed to generate data' });
    } finally {
      setGeneratingData(false);
    }
  }, [fetchWithAuth, selectedIds]);

  const handlePublish = React.useCallback(async () => {
    if (selectedIds.size === 0) {
      setActionMessage({ type: 'error', text: 'Select at least one product.' });
      return;
    }
    setPublishing(true);
    setActionMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: Array.from(selectedIds) }),
        timeoutMs: config.longRequestTimeoutMs,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to publish products');
      }
      const published =
        typeof payload.scheduled === 'number'
          ? payload.scheduled
          : typeof payload.published === 'number'
            ? payload.published
            : selectedIds.size;
      setActionMessage({
        type: 'info',
        text: `Publish queued for ${published} product${published === 1 ? '' : 's'}. Progress is available in Team Jobs.`,
      });
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message || 'Failed to publish products' });
    } finally {
      setPublishing(false);
    }
  }, [fetchWithAuth, selectedIds]);

  const handleOpenProduct = React.useCallback(
    async (product) => {
      if (!product?.id) {
        return;
      }
      setDrawerOpen(true);
      setDrawerLoading(true);
      setSelectedProduct(null);
      try {
        const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${product.id}`, {
          timeoutMs: config.longRequestTimeoutMs,
        });
        if (!response.ok) {
          throw new Error('Failed to load product details');
        }
        const data = await response.json();
        if (!data?.item) {
          throw new Error('Product not found');
        }
        setSelectedProduct(data.item);
      } catch (err) {
        setActionMessage({ type: 'error', text: err.message || 'Failed to load product details' });
        setDrawerOpen(false);
      } finally {
        setDrawerLoading(false);
      }
    },
    [fetchWithAuth, setActionMessage],
  );

  const handleCloseDrawer = React.useCallback(() => {
    setDrawerOpen(false);
    setSelectedProduct(null);
  }, []);

  const handleRowsPerPageChange = React.useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <FormControl fullWidth sx={{ maxWidth: 240 }}>
          <InputLabel id="shop-select-label">Shop</InputLabel>
          <Select
            labelId="shop-select-label"
            label="Shop"
            value={selectedShopId}
            onChange={(event) => setSelectedShopId(event.target.value)}
          >
            {shops.map((shop) => (
              <MenuItem key={shop.id} value={shop.id}>
                {shop.is_template ? 'Templates' : shop.title}{' '}
                {shop.is_template ? '' : shop.sales_channel ? `(${shop.sales_channel})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          placeholder="Search products"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <FormControl fullWidth sx={{ maxWidth: 180 }}>
          <InputLabel id="product-type-filter-label">Product Type</InputLabel>
          <Select
            labelId="product-type-filter-label"
            label="Product Type"
            value={productTypeFilter}
            onChange={(event) => setProductTypeFilter(event.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {productTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ maxWidth: 180 }}>
          <InputLabel id="variant-type-filter-label">Variant Type</InputLabel>
          <Select
            labelId="variant-type-filter-label"
            label="Variant Type"
            value={variantTypeFilter}
            onChange={(event) => setVariantTypeFilter(event.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {variantTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth sx={{ maxWidth: 180 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="unpublished">Unpublished</MenuItem>
          </Select>
        </FormControl>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={selectedIds.size === 0}
            sx={{ minWidth: 120 }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setSendModalOpen(true);
            }}
            disabled={selectedIds.size === 0}
            sx={{ minWidth: 120 }}
          >
            Send
          </Button>
          <Button
            variant="outlined"
            onClick={handleExport}
            disabled={exporting || !selectedShopId}
            sx={{ minWidth: 140 }}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleImportClick}
            disabled={importing}
            sx={{ minWidth: 140 }}
          >
            {importing ? 'Importing…' : 'Import CSV'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleGenerateData}
            disabled={generatingData || selectedIds.size === 0}
            sx={{ minWidth: 160 }}
          >
            {generatingData ? 'Generating…' : 'Generate Data'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handlePublish}
            disabled={publishing || selectedIds.size === 0}
            sx={{ minWidth: 140 }}
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </Stack>
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
          sx={{ alignSelf: { xs: 'flex-start', md: 'stretch' }, minWidth: 160 }}
        >
          {syncing ? 'Syncing…' : 'Sync Printify'}
        </Button>
      </Stack>
      <input
        type="file"
        accept=".csv,text/csv"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleImportChange}
      />

      {actionMessage ? (
        <Alert severity={actionMessage.type} onClose={() => setActionMessage(null)}>
          {actionMessage.text}
        </Alert>
      ) : null}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Checkbox
            size="small"
            indeterminate={isPartiallySelected}
            checked={isAllSelected}
            onChange={(event) => {
              if (event.target.checked) {
                setSelectedIds(new Set(visibleProductIds));
              } else {
                setSelectedIds(new Set());
              }
            }}
            disabled={visibleProductIds.length === 0}
          />
          <Typography variant="body2" color="text.secondary">
            Select all
          </Typography>
        </Stack>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
      </Box>

      {error ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : loading ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="text.secondary">No products synced yet.</Typography>
        </Box>
      ) : viewMode === 'tiles' ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 2,
            }}
          >
                {products.map((product) => {
                  const imageUrl = product.thumbnail_url || '';
                  const isChecked = selectedIds.has(product.id);
                  const tagText = Array.isArray(product.tags)
                    ? product.tags
                        .map((tag) => String(tag || '').trim())
                        .filter(Boolean)
                        .join(', ')
                    : '';
                  const isPublished = isProductPublished(product);
                  return (
                    <Paper
                  key={product.id || product.external_id}
                  variant="outlined"
                  onClick={() => handleOpenProduct(product)}
                  sx={{
                    borderColor: isChecked ? 'primary.main' : 'divider',
                    borderWidth: isChecked ? 2 : 1,
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ position: 'relative', bgcolor: 'grey.100' }}>
                    <Box sx={{ width: '100%', pt: '100%' }} />
                    {imageUrl ? (
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={product.title}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px dashed',
                          borderColor: 'divider',
                          color: 'text.secondary',
                          fontSize: 12,
                        }}
                      >
                        No preview
                      </Box>
                    )}
                    <Checkbox
                      checked={isChecked}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        const next = new Set(selectedIds);
                        if (event.target.checked) {
                          next.add(product.id);
                        } else {
                          next.delete(product.id);
                        }
                        setSelectedIds(next);
                      }}
                      inputProps={{ 'aria-label': `select product ${product.title}` }}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        left: 6,
                        bgcolor: 'rgba(255,255,255,0.85)',
                        borderRadius: 1,
                      }}
                    />
                    <Chip
                      size="small"
                      color={isPublished ? 'success' : 'default'}
                      label={isPublished ? 'Published' : 'Unpublished'}
                      sx={{ position: 'absolute', top: 10, right: 10 }}
                    />
                  </Box>
                    <Stack spacing={0.5} sx={{ p: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" noWrap title={product.title || product.external_id}>
                          {product.title}
                      </Typography>
                      {product.is_template ? <Chip size="small" label="Template" /> : null}
                    </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {product.shop?.title || 'Unknown shop'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={tagText || 'No tags'}
                      >
                        {tagText || 'No tags'}
                      </Typography>
                    </Stack>
                  </Paper>
                );
            })}
          </Box>
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }}>
            <ResizableColGroup columns={PRODUCTS_TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {PRODUCTS_TABLE_COLUMNS.map((column) => {
                  if (column.key === 'select') {
                    return (
                      <TableCell
                        key={column.key}
                        padding="checkbox"
                        sx={{
                          width: getWidth(column.key),
                          minWidth: getWidth(column.key),
                          maxWidth: getWidth(column.key),
                        }}
                      >
                        <Checkbox
                          indeterminate={isPartiallySelected}
                          checked={isAllSelected}
                          onChange={(event) => {
                            if (event.target.checked) {
                              setSelectedIds(new Set(visibleProductIds));
                            } else {
                              setSelectedIds(new Set());
                            }
                          }}
                          inputProps={{ 'aria-label': 'select all products' }}
                        />
                      </TableCell>
                    );
                  }
                  return (
                    <ResizableHeaderCell
                      key={column.key}
                      column={column}
                      getWidth={getWidth}
                      onResizeStart={startColumnResize}
                    />
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => {
                const imageUrl = product.thumbnail_url || '';
                const isChecked = selectedIds.has(product.id);
                const isPublished = isProductPublished(product);
                return (
                  <TableRow
                    key={product.id || product.external_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenProduct(product)}
                    selected={isChecked}
                  >
                    <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={isChecked}
                        onChange={(event) => {
                          const next = new Set(selectedIds);
                          if (event.target.checked) {
                            next.add(product.id);
                          } else {
                            next.delete(product.id);
                          }
                          setSelectedIds(next);
                        }}
                        inputProps={{ 'aria-label': `select product ${product.title}` }}
                      />
                    </TableCell>
                    <TableCell>
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt={product.title}
                          sx={{
                            width: 96,
                            height: 96,
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 96,
                            height: 96,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'divider',
                            color: 'text.secondary',
                            fontSize: 12,
                          }}
                        >
                          No preview
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{product.image_filename || '—'}</TableCell>
                    <TableCell>{product.product_type || '—'}</TableCell>
                    <TableCell>{product.variant_type || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2">{product.title}</Typography>
                        {product.is_template ? <Chip size="small" label="Template" /> : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {product.shop?.title || 'Unknown shop'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {Array.isArray(product.tags) ? product.tags.join(', ') : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={isPublished ? 'success' : 'default'}
                        label={isPublished ? 'Published' : 'Unpublished'}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 25, 50, 100]}
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <ProductDetailDialog
        open={drawerOpen}
        product={selectedProduct}
        loadingProduct={drawerLoading}
        shops={shops}
        loadingShops={loadingShops}
        fetchWithAuth={fetchWithAuth}
        onClose={handleCloseDrawer}
        onSyncComplete={loadProducts}
        onProductChange={(updated) => {
          if (!updated) {
            return;
          }
          setProducts((prev) =>
            prev.map((item) => (item.id === updated.id ? updated : item)),
          );
          setSelectedProduct(updated);
        }}
        productTypeOptions={productTypeOptions}
        variantTypeOptions={variantTypeOptions}
     />
      <SendProductsDialog
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        shops={shops}
        selectedIds={Array.from(selectedIds)}
        currentShopId={selectedShopId}
        fetchWithAuth={fetchWithAuth}
        onSyncComplete={loadProducts}
      />
      <DeleteProductsDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        selectedIds={Array.from(selectedIds)}
        fetchWithAuth={fetchWithAuth}
        onDeleteComplete={() => {
          setSelectedIds(new Set());
        }}
      />
    </Box>
  );
};

export const ProductDetailDialog = ({
  open,
  product,
  loadingProduct,
  shops,
  loadingShops,
  fetchWithAuth,
  onClose,
  onSyncComplete,
  onProductChange,
  productTypeOptions,
  variantTypeOptions,
}) => {
  const [selectedShopIds, setSelectedShopIds] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [resultMessage, setResultMessage] = React.useState(null);
  const [editTitle, setEditTitle] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [editTagsInput, setEditTagsInput] = React.useState('');
  const [savingMetadata, setSavingMetadata] = React.useState(false);
  const [updateMessage, setUpdateMessage] = React.useState(null);
  const [mockupMessage, setMockupMessage] = React.useState(null);
  const [selectingMockupId, setSelectingMockupId] = React.useState(null);
  const [priceRows, setPriceRows] = React.useState([]);
  const [marginInput, setMarginInput] = React.useState('');
  const [priceMessage, setPriceMessage] = React.useState(null);
  const [savingPrices, setSavingPrices] = React.useState(false);
  const [applyingMargin, setApplyingMargin] = React.useState(false);
  const [creatingTemplate, setCreatingTemplate] = React.useState(false);
  const [templateMessage, setTemplateMessage] = React.useState(null);
  const [editProductType, setEditProductType] = React.useState('');
  const [editVariantType, setEditVariantType] = React.useState('');
  const [productDetailTab, setProductDetailTab] = React.useState('mockups');
  const templateSourceId = product?.template_source_id;
  const templateLabel = product?.template_label;
  const templateDisplayName = React.useMemo(() => {
    if (!templateSourceId) {
      return '';
    }
    if (templateLabel) {
      return templateLabel;
    }
    return `${templateSourceId.slice(0, 8)}…`;
  }, [templateLabel, templateSourceId]);

  const parseTagsInput = React.useCallback((input) => {
    if (!input) {
      return [];
    }
    return input
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  }, []);

  const currentTags = React.useMemo(
    () => parseTagsInput(editTagsInput),
    [editTagsInput, parseTagsInput],
  );

  const extractVariantRows = React.useCallback((data) => {
    if (!data) {
      return [];
    }
    const list = Array.isArray(data.variants) ? data.variants : data.Variants || [];
    return list
      .filter((variant) => {
        const enabled = typeof variant.is_enabled === 'boolean' ? variant.is_enabled : variant.IsEnabled;
        return enabled !== false;
      })
      .map((variant) => ({
        id: variant.id || variant.external_id,
        title: variant.title,
        price: variant.price,
        cost: variant.cost,
        is_enabled: variant.is_enabled,
      }));
  }, []);

  React.useEffect(() => {
    setSelectedShopIds([]);
    setResultMessage(null);
    setUpdateMessage(null);
    setMockupMessage(null);
    setSelectingMockupId(null);
    setTemplateMessage(null);
    setCreatingTemplate(false);
    setProductDetailTab('mockups');
    if (product) {
      setEditTitle(product.title || '');
      setEditDescription(product.description || '');
      const tagList = Array.isArray(product.tags) ? product.tags : [];
      setEditTagsInput(tagList.join(', '));
      setEditProductType(product.product_type || '');
      setEditVariantType(product.variant_type || '');
      setPriceRows(extractVariantRows(product));
      setMarginInput('');
      setPriceMessage(null);
    } else {
      setEditTitle('');
      setEditDescription('');
      setEditTagsInput('');
      setEditProductType('');
      setEditVariantType('');
      setPriceRows([]);
      setMarginInput('');
      setPriceMessage(null);
    }
  }, [product, open, extractVariantRows]);

  const syncedShops = Array.isArray(product?.synced_shops) ? product.synced_shops : [];
  const isTemplateProduct = Boolean(product?.is_template || product?.shop?.is_template);
  const currentShopIds = new Set(
    [
      product?.shop?.id,
      ...syncedShops.map((entry) => entry.shop_id),
    ].filter(Boolean),
  );

  const availableShops = shops.filter((shop) => !currentShopIds.has(shop.id));
  const drawerProductTypeOptions = React.useMemo(() => {
    const set = new Set((productTypeOptions || []).filter((opt) => !!opt));
    if (product?.product_type) {
      set.add(product.product_type);
    }
    if (editProductType) {
      set.add(editProductType);
    }
    return Array.from(set).sort();
  }, [productTypeOptions, product?.product_type, editProductType]);
  const drawerVariantTypeOptions = React.useMemo(() => {
    const set = new Set((variantTypeOptions || []).filter((opt) => !!opt));
    if (product?.variant_type) {
      set.add(product.variant_type);
    }
    if (editVariantType) {
      set.add(editVariantType);
    }
    return Array.from(set).sort();
  }, [variantTypeOptions, product?.variant_type, editVariantType]);
  const parsedRawData = React.useMemo(() => {
    const raw = product?.raw_data || product?.RawData || '';
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [product]);
  const formattedRawData = React.useMemo(() => {
    if (parsedRawData) {
      return JSON.stringify(parsedRawData, null, 2);
    }
    const raw = product?.raw_data || product?.RawData || '';
    return raw || 'No data available.';
  }, [parsedRawData, product]);
  const mockups = React.useMemo(() => {
    if (Array.isArray(product?.images)) {
      return product.images;
    }
    if (Array.isArray(parsedRawData?.images)) {
      return parsedRawData.images;
    }
    return Array.isArray(product?.Images) ? product.Images : [];
  }, [parsedRawData?.images, product]);
  const productOptions = React.useMemo(() => {
    const optionList = Array.isArray(product?.options)
      ? product.options
      : Array.isArray(parsedRawData?.options)
        ? parsedRawData.options
        : [];

    const formatLabel = (option) => {
      const base = String(option?.type || option?.name || '').trim();
      if (!base) {
        return 'Option';
      }
      return base
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(/\bColors\b/g, 'Color')
        .replace(/\bSizes\b/g, 'Size');
    };

    return optionList.map((option) => ({
      ...option,
      displayLabel: formatLabel(option),
      values: Array.isArray(option?.values) ? option.values : [],
    }));
  }, [parsedRawData, product?.options]);
  const optionTabs = React.useMemo(() => {
    return productOptions
      .map((option, index) => {
        const values = option.values;
        if (values.length === 0) {
          return null;
        }
        const label = option.displayLabel;
        const tabValue = `option-${index}`;
        const isColorOption = String(option?.type || '').toLowerCase() === 'color';
        return {
          value: tabValue,
          label,
          content: (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">{option?.name || label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {values.length} value{values.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(auto-fill, minmax(160px, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {values.map((value, valueIndex) => {
                  const swatchColors = Array.isArray(value?.colors) ? value.colors.filter(Boolean) : [];
                  const showColorSwatches = isColorOption || swatchColors.length > 0;
                  return (
                    <Paper
                      key={`${tabValue}-${value?.id || value?.title || valueIndex}`}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        minHeight: 72,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {showColorSwatches ? (
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ width: '100%' }}>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {(swatchColors.length > 0 ? swatchColors : ['#d7dbe0']).map((color, colorIndex) => (
                              <Box
                                key={`${value?.id || value?.title || 'value'}-${color}-${colorIndex}`}
                                sx={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  bgcolor: color,
                                  border: (theme) => `1px solid ${theme.palette.divider}`,
                                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.45)',
                                }}
                              />
                            ))}
                          </Stack>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap title={value?.title || ''}>
                              {value?.title || 'Untitled'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {swatchColors.length > 0 ? swatchColors.join(', ') : `ID: ${value?.id || '—'}`}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                        <Stack
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="center"
                          sx={{ width: '100%', textAlign: 'center' }}
                        >
                          <Box
                            sx={{
                              px: 1.25,
                              py: 0.5,
                              borderRadius: 999,
                              bgcolor: 'grey.100',
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              minWidth: 56,
                            }}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              {value?.title || '—'}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            ID: {value?.id || '—'}
                          </Typography>
                        </Stack>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Stack>
          ),
        };
      })
      .filter(Boolean);
  }, [productOptions]);
  const variantMockupByID = React.useMemo(() => {
    const candidatesByVariantID = new Map();

    mockups.forEach((image, imageIndex) => {
      const src = getMockupSource(image);
      if (!src) {
        return;
      }

      const position = String(image?.position || image?.Position || '').toLowerCase();
      const score =
        (image?.is_default ? 100 : 0)
        + (position === 'front' ? 10 : 0)
        + (position === 'other' ? 1 : 0)
        - imageIndex / 1000;
      const variantIDs = getImageVariantIDs(image);

      variantIDs.forEach((variantID) => {
        const key = getVariantLookupKey(variantID);
        if (!key) {
          return;
        }
        const current = candidatesByVariantID.get(key);
        if (!current || score > current.score) {
          candidatesByVariantID.set(key, { src, score });
        }
      });
    });

    const map = new Map();
    candidatesByVariantID.forEach((value, key) => {
      map.set(key, value.src);
    });
    return map;
  }, [mockups]);
  const variantsTab = React.useMemo(() => {
    const variantList = Array.isArray(parsedRawData?.variants)
      ? parsedRawData.variants
      : Array.isArray(product?.variants)
        ? product.variants
        : [];
    const enabledVariants = variantList.filter((variant) => {
      const enabled = typeof variant?.is_enabled === 'boolean' ? variant.is_enabled : variant?.IsEnabled;
      return enabled !== false;
    });
    const optionValueIndexMaps = productOptions.map((option) => {
      const valueIndexMap = new Map();
      option.values.forEach((value, valueIndex) => {
        const numericID = Number(value?.id);
        if (!Number.isNaN(numericID)) {
          valueIndexMap.set(numericID, valueIndex);
        }
      });
      return valueIndexMap;
    });
    const sortedVariants = enabledVariants
      .map((variant) => {
        const optionIDs = (Array.isArray(variant?.options) ? variant.options : Array.isArray(variant?.Options) ? variant.Options : [])
          .map((value) => Number(value))
          .filter((value) => !Number.isNaN(value));
        return {
          variant,
          optionIDs,
          optionIDSet: new Set(optionIDs),
        };
      })
      .sort((left, right) => {
        for (let optionIndex = 0; optionIndex < productOptions.length; optionIndex += 1) {
          const leftRank = optionValueIndexMaps[optionIndex]
            ?.get(left.optionIDs.find((value) => optionValueIndexMaps[optionIndex]?.has(value)))
            ?? Number.MAX_SAFE_INTEGER;
          const rightRank = optionValueIndexMaps[optionIndex]
            ?.get(right.optionIDs.find((value) => optionValueIndexMaps[optionIndex]?.has(value)))
            ?? Number.MAX_SAFE_INTEGER;
          if (leftRank !== rightRank) {
            return leftRank - rightRank;
          }
        }
        return String(left.variant?.title || '').localeCompare(String(right.variant?.title || ''));
      });
    if (enabledVariants.length === 0) {
      return {
        value: 'variants',
        label: 'Variants',
        content: (
          <Typography variant="body2" color="text.secondary">
            No enabled variants available.
          </Typography>
        ),
      };
    }

    return {
      value: 'variants',
      label: 'Variants',
      content: (
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2">Enabled Variants</Typography>
            <Typography variant="body2" color="text.secondary">
              {enabledVariants.length} variant{enabledVariants.length === 1 ? '' : 's'}
            </Typography>
          </Box>
          <Stack spacing={1.5}>
            {sortedVariants.map(({ variant, optionIDSet }, variantIndex) => {
              const variantMockupSrc = variantMockupByID.get(
                getVariantLookupKey(variant?.id || variant?.external_id),
              );
              return (
                <Paper
                  key={`variant-${variant?.id || variant?.external_id || variantIndex}`}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 2 }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {variant?.title || `Variant ${variantIndex + 1}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {variant?.id || variant?.external_id || '—'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {productOptions.map((option, optionIndex) => {
                          const matchedValue = option.values.find((value) => optionIDSet.has(Number(value?.id)));
                          if (!matchedValue) {
                            return null;
                          }
                          const swatchColors = Array.isArray(matchedValue?.colors) ? matchedValue.colors.filter(Boolean) : [];
                          const isColorOption = String(option?.type || '').toLowerCase() === 'color' || swatchColors.length > 0;
                          return (
                            <Paper
                              key={`variant-option-${variant?.id || variantIndex}-${option?.name || optionIndex}`}
                              variant="outlined"
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 2,
                                minWidth: 132,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {isColorOption ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  {(swatchColors.length > 0 ? swatchColors : ['#d7dbe0']).map((color, colorIndex) => (
                                    <Box
                                      key={`variant-swatch-${variant?.id || variantIndex}-${optionIndex}-${colorIndex}`}
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor: color,
                                        border: (theme) => `1px solid ${theme.palette.divider}`,
                                      }}
                                    />
                                  ))}
                                </Stack>
                              ) : null}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {option.displayLabel}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} noWrap title={matchedValue?.title || ''}>
                                  {matchedValue?.title || '—'}
                                </Typography>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Stack>
                    {variantMockupSrc ? (
                      <Box
                        sx={{
                          width: 92,
                          minWidth: 92,
                          height: 92,
                          borderRadius: 2,
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          bgcolor: 'grey.100',
                          overflow: 'hidden',
                          flexShrink: 0,
                          alignSelf: 'center',
                        }}
                      >
                        <Box
                          component="img"
                          src={variantMockupSrc}
                          alt={`${variant?.title || `Variant ${variantIndex + 1}`} mockup`}
                          loading="lazy"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </Box>
                    ) : null}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Stack>
      ),
    };
  }, [parsedRawData, product?.variants, productOptions, variantMockupByID]);
  const detailTabs = React.useMemo(() => [...optionTabs, variantsTab], [optionTabs, variantsTab]);
  const {
    getWidth: getPricingColumnWidth,
    startColumnResize: startPricingColumnResize,
    tableMinWidth: pricingTableMinWidth,
  } = useResizableColumns(PRODUCT_PRICING_TABLE_COLUMNS, {
    storageKey: 'table.product-pricing',
  });

  const originalTitle = product?.title || '';
  const originalDescription = product?.description || '';
  const originalTags = Array.isArray(product?.tags) ? product.tags : [];
  const originalProductType = product?.product_type || '';
  const originalVariantType = product?.variant_type || '';

  const arraysEqual = (a, b) => {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((value, index) => value === b[index]);
  };

  const hasMetadataChanges =
    originalTitle !== editTitle ||
    originalDescription !== editDescription ||
    originalProductType !== editProductType ||
    originalVariantType !== editVariantType ||
    !arraysEqual(originalTags, currentTags);

  const handleToggleShop = (shopId) => {
    setSelectedShopIds((prev) =>
      prev.includes(shopId) ? prev.filter((id) => id !== shopId) : [...prev, shopId],
    );
  };

  const handleSync = async () => {
    if (!selectedShopIds.length) {
      setResultMessage({ type: 'error', text: 'Select at least one target shop.' });
      return;
    }
    setSubmitting(true);
    setResultMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/cross-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_ids: [product.id],
          target_shop_ids: selectedShopIds,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Sync failed');
      }
      const payload = await response.json().catch(() => ({}));
      const queued = typeof payload.target_shops === 'number' ? payload.target_shops : selectedShopIds.length;
      setResultMessage({
        type: 'info',
        text: `Sync queued for ${queued} target shop${queued === 1 ? '' : 's'}. Track progress in Team Jobs.`,
      });
    } catch (err) {
      setResultMessage({ type: 'error', text: err.message || 'Sync failed' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!product) {
      return;
    }
    const defaultLabel = product.template_label || product.title || '';
    const entered = window.prompt('Template name', defaultLabel);
    if (entered === null) {
      return;
    }
    const label = entered.trim();
    setCreatingTemplate(true);
    setTemplateMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${product.id}/template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to create template');
      }
      setTemplateMessage({ type: 'success', text: 'Template created successfully.' });
      await onSyncComplete?.();
    } catch (err) {
      setTemplateMessage({ type: 'error', text: err.message || 'Failed to create template' });
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleSaveMetadata = async () => {
    setSavingMetadata(true);
    setUpdateMessage(null);
    try {
      const payload = {
        title: editTitle,
        description: editDescription,
        tags: currentTags,
        product_type: editProductType,
        variant_type: editVariantType,
      };
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${product.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Update failed');
      }
      const data = await response.json();
      const updatedProduct = data?.item || null;
      if (updatedProduct) {
        onProductChange?.(updatedProduct);
        setEditTitle(updatedProduct.title || '');
        setEditDescription(updatedProduct.description || '');
        const updatedTags = Array.isArray(updatedProduct.tags) ? updatedProduct.tags : [];
        setEditTagsInput(updatedTags.join(', '));
        setEditProductType(updatedProduct.product_type || '');
        setEditVariantType(updatedProduct.variant_type || '');
      }
      setUpdateMessage({ type: 'success', text: 'Product updated successfully.' });
      await onSyncComplete?.();
    } catch (err) {
      setUpdateMessage({ type: 'error', text: err.message || 'Update failed' });
    } finally {
      setSavingMetadata(false);
    }
  };

  const formatMoney = (value) => {
    const dollars = (value || 0) / 100;
    return dollars.toFixed(2);
  };

  const parseMoneyInput = (value) => {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return null;
    }
    return Math.round(parsed * 100);
  };

  const handleSelectMockup = async (image) => {
    if (!image?.id || image.is_default) {
      return;
    }
    setSelectingMockupId(image.id);
    setMockupMessage(null);
    try {
      const response = await fetchWithAuth(
        `${config.apiBaseUrl}/printify/products/${product.id}/images/${image.id}/feature`,
        { method: 'POST', timeoutMs: config.longRequestTimeoutMs },
      );
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to update featured mockup');
      }
      const payload = await response.json();
      const updatedProduct = payload?.item || null;
      if (updatedProduct) {
        onProductChange?.(updatedProduct);
      }
      setMockupMessage({ type: 'success', text: 'Featured mockup updated.' });
    } catch (err) {
      setMockupMessage({ type: 'error', text: err.message || 'Failed to update mockup' });
    } finally {
      setSelectingMockupId(null);
    }
  };

  const handlePriceChange = (variantId, value) => {
    setPriceRows((prev) =>
      prev.map((row) => (row.id === variantId ? { ...row, price: parseMoneyInput(value) ?? row.price } : row)),
    );
  };

  const handleSavePrices = async () => {
    if (!priceRows.length) {
      return;
    }
    setSavingPrices(true);
    setPriceMessage(null);
    try {
      const payload = {
        variants: priceRows
          .filter((row) => row.price > 0)
          .map((row) => ({
            id: row.id,
            price: row.price,
          })),
      };
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${product.id}/pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: config.longRequestTimeoutMs,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to update prices');
      }
      const data = await response.json();
      const updatedProduct = data?.item;
      if (updatedProduct) {
        onProductChange?.(updatedProduct);
        setPriceRows(extractVariantRows(updatedProduct));
      }
      setPriceMessage({ type: 'success', text: 'Prices updated successfully.' });
    } catch (err) {
      setPriceMessage({ type: 'error', text: err.message || 'Failed to update prices' });
    } finally {
      setSavingPrices(false);
    }
  };

  const handleApplyMargin = async () => {
    const marginValue = parseFloat(marginInput);
    if (Number.isNaN(marginValue)) {
      setPriceMessage({ type: 'error', text: 'Enter a valid margin percentage.' });
      return;
    }
    setApplyingMargin(true);
    setPriceMessage(null);
    try {
      const payload = { margin_percent: marginValue };
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${product.id}/pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: config.longRequestTimeoutMs,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Failed to apply margin');
      }
      const data = await response.json();
      const updatedProduct = data?.item;
      if (updatedProduct) {
        onProductChange?.(updatedProduct);
        setPriceRows(extractVariantRows(updatedProduct));
      }
      setPriceMessage({ type: 'success', text: 'Margin applied to all variants.' });
    } catch (err) {
      setPriceMessage({ type: 'error', text: err.message || 'Failed to apply margin' });
    } finally {
      setApplyingMargin(false);
    }
  };

  return (
    <DetailModalShell
      open={open}
      onClose={onClose}
      title={product?.title || 'Product Details'}
      externalId={product?.external_id || product?.id}
      loading={loadingProduct}
      loadingLabel="Loading product..."
      left={
        product ? (
          <MockupsDataTabs
            value={productDetailTab}
            onChange={setProductDetailTab}
            rawData={formattedRawData}
            extraTabs={detailTabs}
            mockupsToolbar={
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Mockups</Typography>
                <Button size="small" variant="outlined" startIcon={<AddPhotoAlternateIcon />} disabled>
                  Upload Image
                </Button>
              </Stack>
            }
            mockupsContent={
              <>
                {mockups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No mockups available.
                  </Typography>
                ) : (
                  <ImageList cols={3} gap={8} sx={{ maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', pr: 1 }}>
                    {mockups.map((img, index) => {
                      const src = getMockupSource(img);
                      if (!src) {
                        return null;
                      }
                      return (
                        <ImageListItem key={`${src}-${index}`} sx={{ position: 'relative' }}>
                          <img
                            src={src}
                            alt={product.title}
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                          />
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSelectMockup(img);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'rgba(0,0,0,0.4)',
                              color: 'common.white',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                            }}
                          >
                            {img.is_default ? (
                              <StarIcon />
                            ) : selectingMockupId === img.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <StarBorderIcon />
                            )}
                          </IconButton>
                        </ImageListItem>
                      );
                    })}
                  </ImageList>
                )}
                {mockupMessage ? (
                  <Alert severity={mockupMessage.type} sx={{ mt: 2 }} onClose={() => setMockupMessage(null)}>
                    {mockupMessage.text}
                  </Alert>
                ) : null}
              </>
            }
          />
        ) : (
          <Typography color="text.secondary">No product selected.</Typography>
        )
      }
      right={
        product ? (
          <>
            {isTemplateProduct ? <Chip size="small" color="secondary" label="Template" sx={{ alignSelf: 'flex-start' }} /> : null}
            <Typography variant="body2" color="text.secondary">
              {product.description}
            </Typography>
            {!isTemplateProduct ? (
              <Stack spacing={1} alignItems="flex-start">
                <Button variant="outlined" size="small" onClick={handleCreateTemplate} disabled={creatingTemplate}>
                  {creatingTemplate ? 'Creating…' : 'Create Template'}
                </Button>
                {templateSourceId ? (
                  <Typography variant="body2" color="text.secondary">
                    Created from template: <strong>{templateDisplayName}</strong>
                  </Typography>
                ) : null}
              </Stack>
            ) : templateLabel ? (
              <Typography variant="body2" color="text.secondary">
                Template name: {templateLabel}
              </Typography>
            ) : null}
            {templateMessage ? (
              <Alert severity={templateMessage.type} onClose={() => setTemplateMessage(null)}>
                {templateMessage.text}
              </Alert>
            ) : null}

            <Divider />
            <Typography variant="subtitle1">Edit Product Details</Typography>
            <Stack spacing={2}>
              <TextField
                label="Title"
                fullWidth
                value={editTitle}
                onChange={(event) => setEditTitle(event.target.value)}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={4}
                value={editDescription}
                onChange={(event) => setEditDescription(event.target.value)}
              />
              <TextField
                label="Tags (comma separated)"
                fullWidth
                value={editTagsInput}
                onChange={(event) => setEditTagsInput(event.target.value)}
                helperText="Example: tag one, tag two"
              />
              <FormControl fullWidth>
                <InputLabel id="edit-product-type-label">Product Type</InputLabel>
                <Select
                  labelId="edit-product-type-label"
                  label="Product Type"
                  value={editProductType}
                  onChange={(event) => setEditProductType(event.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {drawerProductTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="edit-variant-type-label">Variant Type</InputLabel>
                <Select
                  labelId="edit-variant-type-label"
                  label="Variant Type"
                  value={editVariantType}
                  onChange={(event) => setEditVariantType(event.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {drawerVariantTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSaveMetadata}
                  disabled={savingMetadata || !hasMetadataChanges}
                >
                  {savingMetadata ? 'Saving…' : 'Save Changes'}
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setEditTitle(originalTitle);
                    setEditDescription(originalDescription);
                    setEditTagsInput(originalTags.join(', '));
                    setEditProductType(originalProductType);
                    setEditVariantType(originalVariantType);
                    setUpdateMessage(null);
                  }}
                  disabled={savingMetadata || !hasMetadataChanges}
                >
                  Reset
                </Button>
              </Stack>
              {updateMessage ? (
                <Alert severity={updateMessage.type} onClose={() => setUpdateMessage(null)}>
                  {updateMessage.text}
                </Alert>
              ) : null}
            </Stack>

            <Divider />
            <Typography variant="subtitle1">Current Shops</Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {product.shop?.title || 'Unknown shop'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {product.shop?.sales_channel || '—'}
                </Typography>
              </Box>
              {syncedShops.map((shop) => (
                <Box key={shop.shop_id}>
                  <Typography variant="body2" fontWeight={600}>
                    {shop.shop_title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {shop.sales_channel || '—'}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Divider />
            <Typography variant="subtitle1">Sync to other shops</Typography>
            {loadingShops ? (
              <CircularProgress size={24} />
            ) : availableShops.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No additional shops available.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {availableShops.map((shop) => (
                  <FormControlLabel
                    key={shop.id}
                    control={
                      <Checkbox
                        checked={selectedShopIds.includes(shop.id)}
                        onChange={() => handleToggleShop(shop.id)}
                      />
                    }
                    label={`${shop.title} (${shop.sales_channel || 'Unknown'})`}
                  />
                ))}
              </Stack>
            )}

            {resultMessage ? (
              <Alert severity={resultMessage.type} onClose={() => setResultMessage(null)}>
                {resultMessage.text}
              </Alert>
            ) : null}

            <Box sx={{ flexShrink: 0 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1">Prices</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Profit margin (%)"
                  type="number"
                  value={marginInput}
                  onChange={(event) => setMarginInput(event.target.value)}
                  sx={{ maxWidth: 200 }}
                />
                <Button variant="outlined" onClick={handleApplyMargin} disabled={applyingMargin}>
                  {applyingMargin ? 'Applying…' : 'Apply Margin'}
                </Button>
              </Stack>
              {priceRows.length ? (
                <TableContainer sx={{ mb: 2, flexShrink: 0 }}>
                  <Table size="small" sx={{ tableLayout: 'fixed', minWidth: pricingTableMinWidth }} stickyHeader>
                    <ResizableColGroup columns={PRODUCT_PRICING_TABLE_COLUMNS} getWidth={getPricingColumnWidth} />
                    <TableHead>
                      <TableRow>
                        {PRODUCT_PRICING_TABLE_COLUMNS.map((column) => (
                          <ResizableHeaderCell
                            key={column.key}
                            column={column}
                            getWidth={getPricingColumnWidth}
                            onResizeStart={startPricingColumnResize}
                          />
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {priceRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.title}</TableCell>
                          <TableCell>${formatMoney(row.cost)}</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={(row.price ?? 0) / 100}
                              onChange={(event) => handlePriceChange(row.id, event.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { inputMode: 'decimal' },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No variant pricing available.
                </Typography>
              )}
              {priceMessage ? (
                <Alert severity={priceMessage.type} sx={{ mb: 2 }} onClose={() => setPriceMessage(null)}>
                  {priceMessage.text}
                </Alert>
              ) : null}
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" onClick={handleSavePrices} disabled={savingPrices || !priceRows.length}>
                  {savingPrices ? 'Saving…' : 'Save Prices'}
                </Button>
              </Box>
            </Box>

            <Box sx={{ mt: 'auto', mb: 2 }}>
              <Button variant="contained" disabled={submitting || !selectedShopIds.length} onClick={handleSync}>
                {submitting ? 'Syncing…' : 'Sync Selected Shops'}
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Typography color="text.secondary">No product selected.</Typography>
          </Box>
        )
      }
    />
  );
};

const SendProductsDialog = ({
  open,
  onClose,
  shops,
  selectedIds,
  currentShopId,
  fetchWithAuth,
  onSyncComplete,
}) => {
  const [targetShopIds, setTargetShopIds] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [resultMessage, setResultMessage] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setTargetShopIds([]);
      setResultMessage(null);
    }
  }, [open]);

  const availableShops = React.useMemo(
    () => shops.filter((shop) => shop.id !== currentShopId),
    [shops, currentShopId],
  );

  const toggleTarget = (shopId) => {
    setTargetShopIds((prev) =>
      prev.includes(shopId) ? prev.filter((id) => id !== shopId) : [...prev, shopId],
    );
  };

  const handleSend = async () => {
    if (!selectedIds.length) {
      setResultMessage({ type: 'error', text: 'Select at least one product.' });
      return;
    }
    if (!targetShopIds.length) {
      setResultMessage({ type: 'error', text: 'Select at least one destination shop.' });
      return;
    }
    setSubmitting(true);
    setResultMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/cross-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_ids: selectedIds,
          target_shop_ids: targetShopIds,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Send failed');
      }
      const payload = await response.json().catch(() => ({}));
      const queued = typeof payload.products_queued === 'number' ? payload.products_queued : selectedIds.length;
      const targetShops = typeof payload.target_shops === 'number' ? payload.target_shops : targetShopIds.length;
      setResultMessage({
        type: 'info',
        text: `Queued ${queued} product${queued === 1 ? '' : 's'} to sync across ${targetShops} shop${targetShops === 1 ? '' : 's'}. Track progress in Team Jobs.`,
      });
    } catch (err) {
      setResultMessage({ type: 'error', text: err.message || 'Send failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Products</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the shops you want to copy the selected products to.
        </Typography>
        <Stack spacing={1}>
          {availableShops.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No other shops available.
            </Typography>
          ) : (
            availableShops.map((shop) => (
              <FormControlLabel
                key={shop.id}
                control={
                  <Checkbox
                    checked={targetShopIds.includes(shop.id)}
                    onChange={() => toggleTarget(shop.id)}
                  />
                }
                label={`${shop.title} (${shop.sales_channel || 'Unknown'})`}
              />
            ))
          )}
        </Stack>
        {resultMessage ? (
          <Alert severity={resultMessage.type} sx={{ mt: 2 }}>
            {resultMessage.text}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={submitting || !targetShopIds.length || !selectedIds.length}
        >
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteProductsDialog = ({ open, onClose, selectedIds, fetchWithAuth, onDeleteComplete }) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [resultMessage, setResultMessage] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setResultMessage(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!selectedIds.length) {
      setResultMessage({ type: 'error', text: 'No products selected.' });
      return;
    }
    setSubmitting(true);
    setResultMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: selectedIds }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Delete failed');
      }
      const payload = await response.json().catch(() => ({}));
      const queued = typeof payload.queued === 'number' ? payload.queued : selectedIds.length;
      setResultMessage({
        type: 'info',
        text: `Delete queued for ${queued} product${queued === 1 ? '' : 's'}. Track progress in Team Jobs.`,
      });
      await onDeleteComplete?.();
    } catch (err) {
      setResultMessage({ type: 'error', text: err.message || 'Delete failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Delete Products</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary">
          Delete {selectedIds.length} product{selectedIds.length === 1 ? '' : 's'} from all shops? This cannot be undone.
        </Typography>
        {resultMessage ? (
          <Alert severity={resultMessage.type} sx={{ mt: 2 }}>
            {resultMessage.text}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleDelete} disabled={submitting || !selectedIds.length}>
          {submitting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductsPage;
